import { Download } from "lucide-react";
import { downloadCSV } from "@/lib/exporters";

export function Overline({ children, className = "" }) {
  return (
    <div className={`font-num text-xs uppercase tracking-[0.2em] text-slate-500 ${className}`}>
      {children}
    </div>
  );
}

export function SectionShell({ id, num, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 py-16 sm:py-20 border-t border-slate-200 first:border-t-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-baseline gap-4 mb-8">
          {num ? <span className="font-num text-sm text-accent font-semibold">{num}</span> : null}
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {title}
          </h2>
        </div>
        {children}
      </div>
    </section>
  );
}

// A flat white chart/content card following the design spec.
// Pass `csv={{ name, columns, rows }}` to show a per-chart CSV download button.
export function Panel({ title, subtitle, source, children, className = "", testid, csv }) {
  return (
    <div
      data-testid={testid}
      className={`panel-card bg-white border border-slate-200 rounded-xl p-5 sm:p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300 ${className}`}
    >
      {(title || subtitle || csv) && (
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            {title && <h3 className="font-display text-lg font-semibold text-slate-800">{title}</h3>}
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          {csv && (
            <button
              onClick={() => downloadCSV(csv.name, csv.columns, csv.rows)}
              data-testid={`csv-${(csv.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              title="Download this chart's data as CSV"
              className="no-print shrink-0 flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary border border-slate-200 hover:border-primary/40 rounded-md px-2.5 py-1.5 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          )}
        </div>
      )}
      {children}
      {source && <p className="font-num text-[10px] uppercase tracking-wider text-slate-400 mt-4">{source}</p>}
    </div>
  );
}

export function Note({ children, accent = false }) {
  return (
    <div
      className={`text-sm leading-relaxed rounded-lg p-4 mt-6 ${
        accent
          ? "bg-orange-50 border border-orange-200 text-slate-700"
          : "bg-slate-50 border border-slate-200 text-slate-600"
      }`}
    >
      {children}
    </div>
  );
}
